/**
 * UploadSanitiser
 *
 * Top-level upload-pipeline policy gate. Every file intended for upload MUST
 * pass through `sanitiseForUpload` before any further processing. The function:
 *
 *   1. Rejects MIME types not on `ALLOWED_MIME_TYPES` (unsupportedMime).
 *   2. Rejects files over the 25 MB per-upload cap (oversize).
 *   3. Strips metadata — EXIF/ICC/XMP for images, ID3/iTunes for audio.
 *   4. Returns `stripFailed` if stripping throws (corrupt data). The original
 *      bytes are NEVER forwarded on strip failure.
 *
 * Reject-by-default: anything not on the allow-list is blocked. There is no
 * "pass through unrecognised types" escape hatch.
 *
 * Lower layers (server, CDN) may enforce stricter size caps — this layer's
 * 25 MB cap is a safety floor, not a ceiling.
 */

import { stripImageMetadata, type SupportedImageMime } from "./ImageMetadataStripper";
import { stripAudioMetadata, type SupportedAudioMime } from "./AudioMetadataStripper";

// ---------------------------------------------------------------------------
// Allow-list
// ---------------------------------------------------------------------------

/**
 * Exhaustive list of MIME types accepted for upload.
 *
 * Rationale for each category:
 *   Images   — JPEG/PNG/WEBP are stripped via OffscreenCanvas re-encode.
 *              GIF is excluded: re-encoding would lose animation; a GIF-aware
 *              animated-strip path is tracked as a separate TODO.
 *   Audio    — MPEG, MP4 audio, Ogg are stripped via byte-level parsers.
 *   Video    — Excluded from this pipeline; WASM mp4 parser is out-of-scope.
 *              Tracked as a separate TODO. Users cannot upload raw video until
 *              that path ships.
 *   Document — PDF, plain text, JSON are metadata-inert at this level (the
 *              PDF parser and content-scan are in AttachmentScanner, a
 *              separate feature). Listed here so the allow-list is a single
 *              source of truth for what is ever allowed.
 */
export const ALLOWED_MIME_TYPES = [
  // Images
  "image/jpeg",
  "image/png",
  "image/webp",
  // Audio
  "audio/mpeg",
  "audio/mp4",
  "audio/ogg",
  // Documents (metadata-inert at this layer)
  "application/pdf",
  "text/plain",
  "application/json",
] as const;

export type AllowedMime = (typeof ALLOWED_MIME_TYPES)[number];

// Internal sets for fast membership tests
const IMAGE_MIMES = new Set<string>(["image/jpeg", "image/png", "image/webp"]);
const AUDIO_MIMES = new Set<string>(["audio/mpeg", "audio/mp4", "audio/ogg"]);

// ---------------------------------------------------------------------------
// Size cap
// ---------------------------------------------------------------------------

/** 25 MB — enforced before any stripping attempt. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 26_214_400

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export type SanitiseSuccess = {
  readonly ok: true;
  readonly bytes: Uint8Array;
  /** The MIME type of the output (may differ from input for future transcoding paths). */
  readonly mime: string;
};

export type SanitiseFailure = {
  readonly ok: false;
  readonly reason: "unsupportedMime" | "oversize" | "stripFailed";
};

export type SanitiseResult = SanitiseSuccess | SanitiseFailure;

// ---------------------------------------------------------------------------
// sanitiseForUpload — main export
// ---------------------------------------------------------------------------

/**
 * Gate + sanitise `bytes` for upload.
 *
 * ```ts
 * const result = await sanitiseForUpload(fileBytes, file.type);
 * if (!result.ok) {
 *   reportError(result.reason);
 *   return;
 * }
 * await uploadEncrypted(result.bytes, result.mime);
 * ```
 */
export async function sanitiseForUpload(
  bytes: Uint8Array,
  mime: string,
): Promise<SanitiseResult> {
  // 1. Allow-list gate
  if (!ALLOWED_MIME_TYPES.includes(mime as AllowedMime)) {
    return { ok: false, reason: "unsupportedMime" };
  }

  // 2. Size cap
  if (bytes.length > MAX_UPLOAD_BYTES) {
    return { ok: false, reason: "oversize" };
  }

  // 3. Strip metadata
  try {
    let stripped: Uint8Array;

    if (IMAGE_MIMES.has(mime)) {
      stripped = await stripImageMetadata(bytes, mime as SupportedImageMime);
    } else if (AUDIO_MIMES.has(mime)) {
      stripped = stripAudioMetadata(bytes, mime as SupportedAudioMime);
    } else {
      // Document types: no metadata stripping needed at this layer.
      // Pass through a copy so callers can safely dispose of the original.
      stripped = bytes.slice();
    }

    return { ok: true, bytes: stripped, mime };
  } catch {
    // Corrupt image/audio — never forward the original.
    return { ok: false, reason: "stripFailed" };
  }
}
